import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { forwardRef, useId } from 'react'
import { cx } from '@/lib/cx'

type TextFieldProps = ComponentPropsWithoutRef<'input'> & {
  label: ReactNode
  /** Field-level error, typically an Inertia validation message. */
  error?: string
  hint?: ReactNode
}

// Labelled text input for the auth forms: olive focus ring from the kit, an
// accessible error message wired via aria-describedby/aria-invalid.
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, className, id, ...props },
  ref,
) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const errorId = `${inputId}-error`
  const hintId = `${inputId}-hint`

  return (
    <div>
      <label htmlFor={inputId} className="field-label">
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        className={cx('field-input', className)}
        aria-invalid={error != null || undefined}
        aria-describedby={error != null ? errorId : hint != null ? hintId : undefined}
        {...props}
      />
      {hint != null && error == null && (
        <p id={hintId} className="mt-1.5 text-small text-ink-muted">
          {hint}
        </p>
      )}
      {error != null && (
        <p id={errorId} className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
})
